import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { authHeaders, getAutomateApiUrl } from '../automate/apiBase';

// Own slice (not requestsSlice) — deliberately isolated so nothing here can ever
// touch Automate's request state/reducers. See the BRD-to-FRD backend plan.

function rejectApiError(error, fallback, rejectWithValue) {
  const data = error.response?.data;
  return rejectWithValue(data?.error || data?.message || fallback);
}

// Create + kick off generation. `onGenerate(formData, meta)` in BrdToFrd.js already
// builds the FormData with the exact field names the backend expects — this thunk
// just posts it as-is and adds projectId.
export const createBrdToFrdRequest = createAsyncThunk(
  'brdToFrd/createRequest',
  async ({ formData, projectId }, { rejectWithValue }) => {
    try {
      formData.append('projectId', String(projectId));
      const base = getAutomateApiUrl();
      const response = await axios.post(`${base}/api/brd-to-frd/generate`, formData, {
        headers: authHeaders(),
      });
      return response.data;
    } catch (error) {
      return rejectApiError(error, 'Failed to start FRD generation', rejectWithValue);
    }
  }
);

export const fetchBrdToFrdRequestById = createAsyncThunk(
  'brdToFrd/fetchRequestById',
  async (requestId, { rejectWithValue }) => {
    try {
      const base = getAutomateApiUrl();
      const response = await axios.get(`${base}/api/brd-to-frd/${requestId}`, {
        headers: authHeaders(),
      });
      return response.data;
    } catch (error) {
      return rejectApiError(error, 'Failed to fetch FRD request', rejectWithValue);
    }
  }
);

// Resume support: latest non-dismissed request for this project, so a navigate-away
// mid-generation (or a completed doc the user never saw) can be reattached on return.
export const fetchActiveBrdToFrdRequest = createAsyncThunk(
  'brdToFrd/fetchActiveRequest',
  async ({ projectId, excludeIds = [] }, { rejectWithValue }) => {
    try {
      const base = getAutomateApiUrl();
      const response = await axios.get(`${base}/api/brd-to-frd/active`, {
        params: { projectId, excludeIds: excludeIds.join(',') },
        headers: authHeaders(),
      });
      return response.data?.request || null;
    } catch (error) {
      return rejectApiError(error, 'Failed to check for an in-progress request', rejectWithValue);
    }
  }
);

// "Requirement Document" picker — this project's Lens Library sources (the only
// source this picker offers). Each item carries `source` ('lens' | 'own') so the
// picker knows whether it still needs importing before it can be used ('own' means
// it's already a real, reusable document — see importLensBrdDocument below).
export const fetchPreviousBrdDocuments = createAsyncThunk(
  'brdToFrd/fetchPreviousDocuments',
  async (projectId, { rejectWithValue }) => {
    try {
      const base = getAutomateApiUrl();
      const response = await axios.get(`${base}/api/brd-to-frd/documents`, {
        params: { projectId },
        headers: authHeaders(),
      });
      const docs = response.data?.documents || [];
      return docs.map((d) => ({ id: d.id, name: d.name, updatedAt: d.createdAt, source: d.source || 'lens' }));
    } catch (error) {
      return rejectApiError(error, 'Failed to fetch previous documents', rejectWithValue);
    }
  }
);

// Turns one Lens-sourced picker entry into a real, reusable brd_to_frd_documents row —
// called the moment the user selects it (it has no real id yet, just a composite
// "lens:<documentId>" — see BrdToFrdPage.js).
export const importLensBrdDocument = createAsyncThunk(
  'brdToFrd/importLensDocument',
  async ({ projectId, lensDocumentId }, { rejectWithValue }) => {
    try {
      const base = getAutomateApiUrl();
      const formData = new FormData();
      formData.append('projectId', String(projectId));
      formData.append('lensDocumentId', String(lensDocumentId));
      const response = await axios.post(`${base}/api/brd-to-frd/documents/import-lens`, formData, {
        headers: authHeaders(),
      });
      const d = response.data?.document || {};
      return { id: d.id, name: d.name, updatedAt: d.createdAt, source: 'own' };
    } catch (error) {
      return rejectApiError(error, 'Failed to import the Lens document', rejectWithValue);
    }
  }
);

// "Previously Generated" tab — past FRDs for this project, each carrying what it was
// generated from (source filenames / pasted-text preview).
export const fetchBrdToFrdHistory = createAsyncThunk(
  'brdToFrd/fetchHistory',
  async (projectId, { rejectWithValue }) => {
    try {
      const base = getAutomateApiUrl();
      const response = await axios.get(`${base}/api/brd-to-frd/history`, {
        params: { projectId },
        headers: authHeaders(),
      });
      return response.data?.requests || [];
    } catch (error) {
      return rejectApiError(error, 'Failed to fetch previous generations', rejectWithValue);
    }
  }
);

const initialState = {
  previousDocuments: [],
  previousDocumentsLoading: false,
  previousDocumentsError: null,
  history: [],
  historyLoading: false,
  historyError: null,
};

const brdToFrdSlice = createSlice({
  name: 'brdToFrd',
  initialState,
  reducers: {
    resetBrdToFrdState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPreviousBrdDocuments.pending, (state) => {
        state.previousDocumentsLoading = true;
        state.previousDocumentsError = null;
      })
      .addCase(fetchPreviousBrdDocuments.fulfilled, (state, action) => {
        state.previousDocumentsLoading = false;
        state.previousDocuments = action.payload;
      })
      .addCase(fetchPreviousBrdDocuments.rejected, (state, action) => {
        state.previousDocumentsLoading = false;
        state.previousDocumentsError = action.payload;
      })
      .addCase(fetchBrdToFrdHistory.pending, (state) => {
        state.historyLoading = true;
        state.historyError = null;
      })
      .addCase(fetchBrdToFrdHistory.fulfilled, (state, action) => {
        state.historyLoading = false;
        state.history = action.payload;
      })
      .addCase(fetchBrdToFrdHistory.rejected, (state, action) => {
        state.historyLoading = false;
        state.historyError = action.payload;
      });
  },
});

export const { resetBrdToFrdState } = brdToFrdSlice.actions;
export default brdToFrdSlice.reducer;
